/**
 * What the crew knows, Sections (slice 6d spec §8.5; Paper A5, A5b, A5c):
 * Facts, Notes and any other lines, one line changed at a time. On a desktop
 * a line opens in place with Save, Cancel and Remove; on a phone it opens
 * Change a fact, a bottom sheet. Each save is the whole file, from the version
 * the page has, with only that line changed (`applyEdit`). A crew change from
 * the last 7 days shows under its line, in copper, with Undo.
 */
import { Button } from "@crew/ui/components/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@crew/ui/components/drawer";
import { Textarea } from "@crew/ui/components/textarea";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { ChevronRight, Pencil, Plus } from "lucide-react";
import type { ReactNode } from "react";
import type { Me } from "../../app/me";
import type { BriefFile } from "./brief-api";
import type { LineEditor } from "./use-line-editor";
import { DocPicker, MentionText } from "./mention-parts";
import { useMentionBox } from "./use-mention-box";
import {
  authorLabel,
  crewChange,
  crewChangeLine,
  dayWord,
  editTarget,
  lastChange,
  lengthLine,
  noteDay,
  parseBrief,
  tooLong,
  type BriefChange,
  type BriefLine,
  type LineEdit,
} from "./brief";

const LINE_MAX = 400;

type Undo = { run: (version: number) => void; busy: boolean };

/** The Area the brief is for, and each mentioned doc's title now (spec §5.5). */
type Mentions = {
  area: { id: string; slug: string; label: string };
  titles: ReadonlyMap<string, string>;
};

type Shared = { ed: LineEditor; recent: BriefChange[]; me: Me; undo: Undo } & Mentions;

const same = (e: LineEdit | null, section: string, n: number | null) =>
  e !== null && e.section === section && e.n === n;

export function SectionsView({
  file,
  onText,
  ...rest
}: Shared & { file: BriefFile; onText?: () => void }) {
  const mobile = useIsMobile();
  const parts = parseBrief(file.content);
  const shared = { ...rest, inline: !mobile };
  return (
    <div className="flex flex-col gap-8" data-brief-sections>
      <Group title="Facts" line="What's true now." data-brief-facts>
        {parts.facts.map((line, n) => (
          <LineRow key={`${line.at}:${line.raw}`} line={line} section="facts" n={n} {...shared} />
        ))}
        <AddRow section="facts" {...shared}>
          Add a fact
        </AddRow>
      </Group>
      <Group
        title="Notes"
        line="One line each, dated. Anything that isn't a fact."
        short="One line each, dated"
        data-brief-notes
      >
        {parts.notes.map((line, n) => (
          <LineRow key={`${line.at}:${line.raw}`} line={line} section="notes" n={n} {...shared} />
        ))}
        <AddRow section="notes" {...shared}>
          Add a note…
        </AddRow>
      </Group>
      {parts.other.length > 0 ? <OtherLines lines={parts.other} onText={onText} /> : null}
      {mobile ? <ChangeSheet file={file} {...shared} /> : null}
    </div>
  );
}

function Group({
  title,
  line,
  short = line,
  children,
  ...props
}: {
  title: string;
  line: string;
  /** A phone's, shorter (A5b). */
  short?: string;
  children: ReactNode;
  [data: `data-${string}`]: boolean;
}) {
  return (
    <section className="flex flex-col gap-3" {...props}>
      <h2 className="flex flex-wrap items-baseline justify-between gap-x-3 md:justify-start">
        <span className="text-lead font-semibold text-foreground">{title}</span>
        <span className="text-meta text-muted-foreground md:hidden">{short}</span>
        <span className="hidden text-meta text-muted-foreground md:inline">{line}</span>
      </h2>
      <ul className="flex flex-col divide-y divide-border rounded-xl bg-card ring-1 ring-border">
        {children}
      </ul>
    </section>
  );
}

type RowProps = Shared & { inline: boolean };

function LineRow({
  line,
  section,
  n,
  ...props
}: RowProps & { line: BriefLine; section: "facts" | "notes"; n: number }) {
  const { ed, inline } = props;
  if (inline && same(ed.editing, section, n)) {
    return <InlineEditor {...props} section={section} existing />;
  }
  return (
    <li data-brief-line={line.text} className="group/line relative">
      <button
        type="button"
        onClick={() => ed.open({ section, n, draft: line.text })}
        className="flex w-full items-start gap-3 px-4 py-3.5 text-left outline-none hover:bg-foreground/[0.02] focus-visible:ring-2 focus-visible:ring-ring md:px-5"
      >
        <Bullet />
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-body text-foreground">
            <MentionText text={line.text} titles={props.titles} plain />
          </span>
          {line.date ? (
            <span className="text-meta text-muted-foreground md:hidden">{noteDay(line.date)}</span>
          ) : null}
        </span>
        <LineAside line={line} {...props} />
      </button>
      <CrewChange raw={line.raw} {...props} />
    </li>
  );
}

/** A desktop's hover: who changed it last and a pencil; a note's date; a phone's chevron. */
function LineAside({ line, recent, me }: RowProps & { line: BriefLine }) {
  const last = lastChange(line.raw, recent);
  return (
    <span className="flex shrink-0 items-center gap-3 pt-0.5 text-meta text-muted-foreground">
      {last ? (
        <span className="hidden opacity-0 group-hover/line:opacity-100 md:inline" data-last-change>
          {authorLabel(last.updated_by, me.user.id)} ·{" "}
          {dayWord(last.updated_at, me.workspace.timezone)}
        </span>
      ) : null}
      {line.date ? (
        <span className="hidden w-12 text-right md:inline">{noteDay(line.date)}</span>
      ) : null}
      <Pencil
        aria-hidden
        className="hidden size-3.5 opacity-0 group-hover/line:opacity-100 md:block"
      />
      <ChevronRight aria-hidden className="size-4 md:hidden" />
    </span>
  );
}

const Bullet = () => (
  <span aria-hidden className="mt-[9px] size-1 shrink-0 rounded-full bg-muted-foreground" />
);

/** "Chief of Staff changed this today. Was $2,500." in copper, with Undo. */
function CrewChange({ raw, recent, me, undo }: RowProps & { raw: string }) {
  const found = crewChange(raw, recent);
  if (!found) return null;
  const { change, was } = found;
  const when = dayWord(change.updated_at, me.workspace.timezone);
  return (
    <p
      data-crew-change={change.version}
      className="-mt-2 flex flex-wrap items-baseline gap-x-2 pr-4 pb-3 pl-8 text-meta text-copper md:pr-5 md:pl-9"
    >
      <span>{crewChangeLine(change.updated_by.name, when, was)}</span>
      {change.can_undo ? (
        <Button
          variant="link"
          size="xs"
          className="h-auto px-0 text-foreground"
          disabled={undo.busy}
          onClick={() => undo.run(change.version)}
        >
          Undo
        </Button>
      ) : null}
    </p>
  );
}

function AddRow({
  section,
  children,
  ...props
}: RowProps & { section: "facts" | "notes"; children: ReactNode }) {
  const { ed, inline } = props;
  if (inline && same(ed.editing, section, null)) {
    return <InlineEditor {...props} section={section} existing={false} />;
  }
  return (
    <li>
      <button
        type="button"
        data-brief-add={section}
        onClick={() => ed.open({ section, n: null, draft: "" })}
        className="flex w-full items-center gap-3 px-4 py-3 text-left text-body text-copy outline-none hover:bg-foreground/[0.02] focus-visible:ring-2 focus-visible:ring-ring md:px-5"
      >
        <Plus aria-hidden className="size-4" />
        {children}
      </button>
    </li>
  );
}

const nameOf = (section: "facts" | "notes") => (section === "facts" ? "fact" : "note");

/** The line in one text box, with Save, Cancel and Remove (A5). */
function InlineEditor({
  section,
  existing,
  ...props
}: RowProps & {
  section: "facts" | "notes";
  existing: boolean;
}) {
  const { ed } = props;
  return (
    <li className="flex flex-col gap-3 bg-background/60 px-4 py-4 md:px-5" data-brief-editing>
      <LineBox
        {...props}
        label={existing ? `Change this ${nameOf(section)}` : `Add a ${nameOf(section)}`}
      />
      <EditProblems ed={ed} />
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={!canSave(ed)} onClick={ed.save}>
          {ed.busy ? "Saving…" : "Save"}
        </Button>
        <Button size="sm" variant="ghost" disabled={ed.busy} onClick={ed.cancel}>
          Cancel
        </Button>
        {existing ? (
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto text-destructive"
            disabled={ed.busy}
            onClick={ed.remove}
          >
            Remove
          </Button>
        ) : null}
      </div>
    </li>
  );
}

const canSave = (ed: LineEditor) =>
  Boolean(ed.editing?.draft.trim()) && !ed.busy && !tooLong(ed.length);

/**
 * The line's one text box. Typing @ opens the doc picker under it (spec §5.5):
 * a pick puts `@Title` in the line, which the save writes as the doc's link;
 * Enter takes the first doc while the picker is open, Escape closes it.
 */
function LineBox({
  ed,
  label,
  area,
  me,
}: Pick<RowProps, "ed" | "area" | "me"> & { label: string }) {
  const m = useMentionBox(ed);
  return (
    <div className="relative flex flex-col gap-1.5">
      <Textarea
        ref={m.box}
        aria-label={label}
        autoFocus
        rows={2}
        maxLength={LINE_MAX}
        value={m.draft}
        onChange={(e) => m.change(e.target.value, e.target.selectionStart)}
        onSelect={(e) => m.moved(e.currentTarget.selectionStart)}
        onKeyDown={m.key}
        className="min-h-0 text-base md:text-body"
      />
      {m.query !== null ? (
        <div className="z-20 md:absolute md:top-full md:left-0 md:mt-1 md:w-[380px]">
          <DocPicker query={m.query} area={area} me={me} onPick={m.pick} first={m.first} />
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Type @ to point to a doc.</p>
      )}
    </div>
  );
}

/** A newer brief it ran into, past 4,000, or a refused save. */
function EditProblems({ ed }: { ed: LineEditor }) {
  const long = tooLong(ed.length);
  return (
    <>
      {ed.conflict ? (
        <p role="alert" data-brief-conflict className="text-meta text-copper">
          Someone changed this since. Here's the brief as it is now.
        </p>
      ) : null}
      {long ? <p className="text-meta text-brick">{long}</p> : null}
      {ed.error ? <p className="text-meta text-brick">{ed.error}</p> : null}
    </>
  );
}

/** Change a fact (A5c): the phone's bottom sheet, with Cancel and Save in its header. */
function ChangeSheet({ ed, recent, me, file, ...props }: RowProps & { file: BriefFile }) {
  const target = editTarget(file.content, ed.editing, recent);
  return (
    <Drawer open={ed.editing !== null} onOpenChange={(open) => (open ? null : ed.cancel())}>
      <DrawerContent className="pb-[env(safe-area-inset-bottom)]" data-brief-sheet>
        <SheetHeader ed={ed} title={target.title} />
        <div className="flex flex-col gap-3 px-4 pb-6">
          {ed.editing ? <LineBox ed={ed} area={props.area} me={me} label={target.title} /> : null}
          <p className="text-right text-xs text-muted-foreground tabular">
            {lengthLine(ed.length, true)}
          </p>
          <EditProblems ed={ed} />
          <SheetLastChange change={target.last} me={me} />
          {target.existing ? <RemoveLine ed={ed} noun={target.noun} /> : null}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function SheetHeader({ ed, title }: { ed: LineEditor; title: string }) {
  return (
    <>
      <DrawerHeader className="flex-row items-center justify-between gap-2 pb-4">
        <Button variant="ghost" size="sm" disabled={ed.busy} onClick={ed.cancel}>
          Cancel
        </Button>
        <DrawerTitle>{title}</DrawerTitle>
        <Button variant="ghost" size="sm" disabled={!canSave(ed)} onClick={ed.save}>
          {ed.busy ? "Saving…" : "Save"}
        </Button>
      </DrawerHeader>
      <DrawerDescription className="sr-only">
        One line. The crew reads it whenever it works here.
      </DrawerDescription>
    </>
  );
}

function RemoveLine({ ed, noun }: { ed: LineEditor; noun: string }) {
  return (
    <div className="mt-1 border-t border-border pt-2">
      <Button
        variant="ghost"
        className="w-full text-destructive"
        disabled={ed.busy}
        onClick={ed.remove}
      >
        Remove this {noun}
      </Button>
    </div>
  );
}

/** "Last changed by Nadia · today", when a recent change wrote the line. */
function SheetLastChange({ change, me }: { change: BriefChange | null; me: Me }) {
  if (!change) return null;
  return (
    <p className="text-meta text-muted-foreground">
      Last changed by {authorLabel(change.updated_by, me.user.id).replace(/^You$/, "you")} ·{" "}
      {dayWord(change.updated_at, me.workspace.timezone)}
    </p>
  );
}

/** Lines outside the shape, kept as written (spec §5.2), and the way to them in Text. */
function OtherLines({ lines, onText }: { lines: BriefLine[]; onText?: () => void }) {
  return (
    <section className="flex flex-col gap-3" data-brief-other>
      <h2 className="flex flex-wrap items-baseline gap-x-3">
        <span className="text-lead font-semibold text-foreground">Other lines</span>
        <span className="text-meta text-muted-foreground">
          Kept as written.{" "}
          {onText ? (
            <Button variant="link" size="xs" className="h-auto px-0" onClick={onText}>
              Change them in Text
            </Button>
          ) : null}
        </span>
      </h2>
      <pre className="overflow-x-auto rounded-xl bg-card px-4 py-3 font-mono text-xs leading-6 whitespace-pre-wrap text-copy ring-1 ring-border md:px-5">
        {lines.map((l) => l.raw).join("\n")}
      </pre>
    </section>
  );
}
